/* global newrelic */
import React, {
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import hasDom from 'has-dom';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

// AXL
import { eventBus } from '@atc/cai-event-bus';
import { injectScript } from '@atc/dom-fns';
import { useFeatures } from '@atc/features';
import { waitUntil } from '@atc/wait-until';

import { Text } from 'reaxl';
import { InventoryImagePlaceholder } from 'reaxl-molecules';
import { Payment } from 'reaxl-wallet';

import { pauseAudioEye } from '@/utilities/pauseAudioEye';

import { getVideoPlayList } from '@/actions/vdp/remoteActions';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpVideoDuck from '@/ducks/vdp/vdpVideoDuck.js';

import SaveAndShareContainer from '@/containers/vdp/SaveAndShareContainer';

import CalculatorJumpLink from '@/components/vehicledetails/CalculatorJumpLink';

import FullscreenViewer from './FullscreenViewer';
import getShowcaseOverlayData from './getShowcaseOverlayData';
import MediaGalleryCTAs from './MediaGalleryCTAs';
import MediaGalleryModal from './MediaGalleryModal';
import MediaNavigator from './MediaNavigator';
import MediaShowcase from './MediaShowcase';
import MediaSlider from './MediaSlider';
import tabTitles from './tabTitles';
import useMediaGalleryAnalytics from './useMediaGalleryAnalytics';

const VERIFY_SPIN_URL = 'https://verify.flickfusion.net/ff_verify_spin.php';

const SPIN360_SELECTORS = {
    SPIN360: '#spin360',
    SPIN360_BTN: '#spin360-btn',
    VEHICLE_RECONDITIONING: '#vehicle-reconditioning-see-details',
    LS360_KEY: '179917CE-18CF-4CEE-9562-8EF99A01D423',
};

/**
 * Injects the 360 spin script into the DOM and configures the 360 spin viewer
 *
 * @async
 * @function inject360Spin
 * @param {string} spin360Script - URL of the 360 spin script to inject
 * @param {string} fyuseId - ID of the Fyuse 360 view
 * @param {React.MutableRefObject} spin360ScriptRef - Reference to the injected script element
 * @param {Function} handleReconditioningClick - Click handler for reconditioning details
 * @param {Function} setSpin360EventType - Function to set up event listeners based on 360 type
 * @param {string} vin - Vehicle Identification Number
 * @returns {Promise<void>}
 */
const inject360Spin = async (spin360Script, fyuseId, spin360ScriptRef, handleReconditioningClick, setSpin360EventType, vin) => {
    if (!spin360ScriptRef?.current?.src?.includes(spin360Script)) {
        if (spin360ScriptRef.current) {
            spin360ScriptRef.current.remove();
        }
        spin360ScriptRef.current = await injectScript(spin360Script, { rejectOnError: true }).catch(() => {
            if (window && window.newrelic) {
                newrelic.recordCustomEvent('VI360PlayerEvent', {
                    Error: 'Fail to load spin 360 script: ' + spin360Script,
                    message: 'Fail to load spin 360 script',
                    // Add the VIN to the payload
                    VIN: vin,
                });
            } else {
                // eslint-disable-next-line no-console
                console.error('Fail to load spin 360 script');
            }
        });
    }

    // wait for media player to render
    waitUntil(
        () => !!document?.getElementById(SPIN360_SELECTORS.SPIN360.substring(1)) || !!document?.getElementById(SPIN360_SELECTORS.SPIN360_BTN.substring(1)),
        () => {
            // If only the 360 Tour button is present (mobile mode, no 360 thumbnail), inject LS360 script for #spin360-btn only and return early
            if (!document.getElementById(SPIN360_SELECTORS.SPIN360.substring(1)) && document.getElementById(SPIN360_SELECTORS.SPIN360_BTN.substring(1))) {
                waitUntil(
                    () => typeof window.LS360 !== 'undefined',
                    () => window.LS360(
                        SPIN360_SELECTORS.LS360_KEY,
                        [SPIN360_SELECTORS.SPIN360_BTN],
                        { val: fyuseId },
                        { button: 1, append: 1 }
                    ),
                    100,
                    5000
                );
                setSpin360EventType();
                return;
            }

            // wait for vehicle reconditioning see details link to render
            waitUntil(() => !!document?.getElementById(SPIN360_SELECTORS.VEHICLE_RECONDITIONING.substring(1)), () => {
            // if link is present, call the LS360 script for both media player and link
                waitUntil(
                    () => typeof window.LS360 !== 'undefined',
                    () => window.LS360(SPIN360_SELECTORS.LS360_KEY, [SPIN360_SELECTORS.VEHICLE_RECONDITIONING, SPIN360_SELECTORS.SPIN360, SPIN360_SELECTORS.SPIN360_BTN], { val: fyuseId }, { button: 1, append: 1 }),
                    100,
                    5000
                );
                if (handleReconditioningClick) {
                    const reconditioningDetail = document.getElementById(SPIN360_SELECTORS.VEHICLE_RECONDITIONING.substring(1));
                    reconditioningDetail.addEventListener('click', handleReconditioningClick);
                }
            }, 100, 3000, () => {
            // if link is not present after 3 seconds, call LS360 script for only the media player
                waitUntil(
                    () => typeof window.LS360 !== 'undefined',
                    () => window.LS360(SPIN360_SELECTORS.LS360_KEY, [SPIN360_SELECTORS.SPIN360, SPIN360_SELECTORS.SPIN360_BTN], { val: fyuseId }, { button: 1, append: 1 }),
                    100,
                    5000
                );
            });

            setSpin360EventType();
        },
        100,
        5000
    );
};

/**
 * Injects the Fyusion 360 script into the DOM
 *
 * @async
 * @function injectFyusion
 * @param {string} fyusionScript - URL of the Fyusion script to inject
 * @param {React.MutableRefObject} fusionScriptRef - Reference to the injected script element
 * @returns {Promise<void>}
 */
const injectFyusion = async (fyusionScript, fusionScriptRef) => {
    if (!fusionScriptRef?.current?.src?.includes(fyusionScript)) {
        if (fusionScriptRef.current) {
            fusionScriptRef.current.remove();
        }
        fusionScriptRef.current = await injectScript(fyusionScript);
    }
};

/**
 * Media Gallery component for displaying vehicle images, videos, and 360° views
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.emailCTAProps - Properties for the email call-to-action button
 * @param {Function} props.phoneClickHandler - Handler for phone number click events
 * @returns {React.ReactElement} - Rendered MediaGallery component
 */
function MediaGallery({
    emailCTAProps = {},
    phoneClickHandler,
}) {
    const dispatch = useDispatch();

    const {
        fyuseId,
        fyusion,
        images = {},
        listingTitle = '',
        listingType,
        mainImageIsStock,
        owner = {},
        hasPlaylist,
        id: inventoryId,
        pricingDetail = {},
        paymentServices: {
            dealerSettings: {
                leaseEnabled,
            } = {},
        } = {},
        vin,
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const videos = useSelector(vdpVideoDuck.selectors.getActiveResults);

    const imageIndex = images?.primary || 0;
    const firstImage = images?.sources?.[imageIndex];
    const showGhostImage = (listingType === 'Used' && owner?.location?.address?.state === 'WA' && mainImageIsStock) || !firstImage;

    const photoCount = Array.isArray(images?.sources) ? images.sources.length : 0;
    const videoCount = Array.isArray(videos) ? videos.length : 0;

    // payment
    const financePayment = useSelector((state) => paymentsDuck.selectors.getLowestPencilingPaymentByListingId(state, inventoryId)?.payment);
    const listingLeasePayments = useSelector((state) => paymentsDuck.selectors.getListingLeasePayments(state)) || {};
    const paymentOption = useSelector((state) => paymentsDuck.selectors.getPaymentOption(state));
    const leasePayment = leaseEnabled && paymentOption === 'lease' && listingLeasePayments[inventoryId]?.payment;
    const {
        media_slider: [isMediaSliderEnabled],
        nds_calc: [isNDSCalcEnabled],
        spin_360: [isSpinEnabled, {
            script_url: spin360Script,
            show360indicator: showSpin,
        }],
        fyusion: [isFyusionEnabled, {
            script_url: fyusionScript,
            showIndicator: showFyusionSpin,
        }],
        pause_media_gallery_audio_eye: [pauseMediaGalleryAudioEye, { length: pauseMediaGalleryAudioEyeLength }],
    } = useFeatures([
        'media_slider',
        'nds_calc',
        'spin_360',
        'fyusion',
        'pause_media_gallery_audio_eye',
    ]);

    const { privateSeller: isPrivateSeller, hideEmailForm } = owner;

    const sendAnalyticsEvent = useMediaGalleryAnalytics();
    const device = useDevice();
    const { mediaType } = device;

    const isXs = _get(device, 'is.xs', true);
    const isSm = _get(device, 'is.sm', true);
    const isLg = _get(device, 'is.lg', true);

    const relocateCTAs = !isPrivateSeller && isXs;

    const selectedTab = useRef(tabTitles.IMAGES);
    const fullScreenIndex = useRef(0);
    const spin360ScriptRef = useRef(null);
    const fusionScriptRef = useRef(null);

    const [showOverlay, setShowOverlay] = useState(false);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [sliderIndex, setSliderIndex] = useState(1);
    const isVI360Ref = useRef(false);
    const vi360StartTimeRef = useRef(null); // Ref to store start time

    useEffect(() => {
        setSliderIndex(1);
        if (hasPlaylist) {
            dispatch(getVideoPlayList(inventoryId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryId]);

    const updateSelectedTab = useCallback((type = tabTitles.IMAGES) => {
        if (selectedTab && selectedTab.current) {
            selectedTab.current = type;
        }
    }, []);

    const hasId = isSpinEnabled && spin360Script && showSpin && !!fyuseId;
    const hasFyusion = fyusion && isFyusionEnabled && showFyusionSpin;

    // load on page load and let fyusion script determine if
    // the main image should display the spin indicator
    const shouldLoadIrrespectiveOfId = isSpinEnabled && spin360Script && !showSpin;

    const handleSnap360Click = useCallback(() => {
        sendAnalyticsEvent('click', 'snap360Click');
    }, [sendAnalyticsEvent]);

    const handleVI360Click = useCallback(() => {
        sendAnalyticsEvent('click', 'vi360Click');
    }, [sendAnalyticsEvent]);

    const recordNRloadingTime = useCallback((event) => {
        const { data = {} } = event;
        if (data?.event === 'evs-vehicle-event' && window?.newrelic) {
            const { payload = {} } = data;
            const { event_action: eventAction, event_action_result: eventActionResult } = payload;
            // Add the VIN with uppercase to the payload
            payload.VIN = vin;
            // if there is event_action it means the player is initialized
            // if there is event_action_result it means the player is loaded
            if (eventAction) {
                vi360StartTimeRef.current = Date.now(); // Store start time in ref
                window.newrelic.recordCustomEvent('VI360PlayerInit', payload);
            } else if (eventActionResult) {
                const loadingTime = Date.now() - vi360StartTimeRef.current; // Calculate duration
                window.newrelic.recordCustomEvent('VI360PlayerLoadingTime', { ...payload, loadingTime });
            }
        }
    }, [vin]);

    const handleReconditioningClick = useCallback(() => {
        sendAnalyticsEvent('click', 'inventoryVehicleConditionHighlightsClick');
    }, [sendAnalyticsEvent]);

    const setSpin360EventType = useCallback(() => {
        const spin360 = document.getElementById(SPIN360_SELECTORS.SPIN360.substring(1));
        waitUntil(() => typeof window.requestDataJSONP !== 'undefined', () => {
            try {
                window.requestDataJSONP(`${VERIFY_SPIN_URL}?video_fkey=${fyuseId}`, 'jsoncallback', (result) => {
                    if (result?.spin?.data?.length && result.spin.data[0].ece_exp) {
                        spin360.addEventListener('click', handleVI360Click);
                        isVI360Ref.current = true;
                        window.addEventListener('message', recordNRloadingTime);
                    } else {
                        spin360.addEventListener('click', handleSnap360Click);
                    }
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error while calling verify spin 360 media for tagging: ', error);
            }
        }, 500, 5000);
    }, [fyuseId, handleVI360Click, recordNRloadingTime, handleSnap360Click]);

    const loadSpin = useCallback((currentIndex) => {
        if (currentIndex === 0 && hasFyusion) {
            injectFyusion(fyusionScript, fusionScriptRef, vin);
        }

        if (currentIndex === 0 && (hasId || shouldLoadIrrespectiveOfId)) {
            const requestParams = fyuseId
                ? `fyuseId=${fyuseId}&supportsEmailLead=${!hideEmailForm}`
                : `vin=${vin}`;
            const scriptUrl = spin360Script + requestParams;

            inject360Spin(scriptUrl, fyuseId, spin360ScriptRef, handleReconditioningClick, setSpin360EventType, vin);
        }
    }, [hasFyusion, hasId, shouldLoadIrrespectiveOfId, fyusionScript, vin, fyuseId, spin360Script, handleReconditioningClick, setSpin360EventType, hideEmailForm]);

    useEffect(() => {
        loadSpin(0);
        return () => {
            if (hasDom()) {
                // this is for stitched 360 only (at_ford.js)
                // set global variable back to false so we can reload 360 on another VDP
                window.flickButtonScriptLoaded = false;
                window.is360Available = false;
            }

            const spin360 = document.getElementById(SPIN360_SELECTORS.SPIN360.substring(1));
            if (spin360) {
                // remove event after component dismount
                if (isVI360Ref.current) {
                    spin360.removeEventListener('click', handleVI360Click);
                    window.removeEventListener('message', recordNRloadingTime);
                    isVI360Ref.current = null;
                } else {
                    spin360.removeEventListener('click', handleSnap360Click);
                }
            }

            const reconditioningDetail = document.getElementById(SPIN360_SELECTORS.VEHICLE_RECONDITIONING.substring(1));
            if (reconditioningDetail) {
                reconditioningDetail.removeEventListener('click', handleReconditioningClick);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryId]);

    const updateFullScreenIndex = useCallback((index) => {
        if (fullScreenIndex?.current !== undefined) {
            fullScreenIndex.current = index;
        }
    }, []);

    const openOverlay = useCallback(({ tab = tabTitles.IMAGES } = {}) => {
        updateSelectedTab(tab);
        setShowOverlay(true);
    }, [updateSelectedTab]);

    const closeOverlay = useCallback(() => {
        setShowOverlay(false);
        updateSelectedTab(tabTitles.IMAGES);
    }, [updateSelectedTab]);

    const openFullScreen = useCallback(({ index, triggerSpin } = {}) => {
        if (index >= 0) {
            updateFullScreenIndex(index);
        }
        setShowFullScreen(true);

        // prevent page scroll when full screen is open
        document.body.style.overflow = 'hidden';

        if (triggerSpin) {
            loadSpin(index);
        }
    }, [updateFullScreenIndex, loadSpin]);

    const closeFullScreen = useCallback(({ index = 0 } = {}) => {
        if (index >= 0) {
            updateFullScreenIndex(index);
        }
        setShowFullScreen(false);

        // reset page scroll when full screen is closed
        document.body.style.overflow = '';

    }, [updateFullScreenIndex, setShowFullScreen]);

    const sendShowcaseItemClick = useCallback((event = {}, index, title) => {
        sendAnalyticsEvent('click', 'inventoryMediaViewerClick', {
            event,
            index,
            title,
            isPsxVdp: isPrivateSeller,
        });
    }, [sendAnalyticsEvent, isPrivateSeller]);

    const handleNDSCTAClick = useCallback((event, label, location) => {
        closeOverlay();
        closeFullScreen();
        sendAnalyticsEvent('click', 'mediaGalleryNdsCTAClick', { event, label, location });
        dispatch(
            paymentsDuck.creators.updateMyWalletInteractions({
                paymentEmphasis: true,
            })
        );
    }, [closeOverlay, closeFullScreen, sendAnalyticsEvent, dispatch]);

    const handleSliderImageClick = useCallback(async (event, index) => {
        await interactionResponse();
        openFullScreen({ index });
        event.stopPropagation();
    }, [openFullScreen]);

    const closeMediaGalleryModal = useCallback(() => {
        closeOverlay();
        closeFullScreen();
    }, [closeOverlay, closeFullScreen]);

    useEffect(() => {
        // Used to close the mediaGallery modal when psxCTA is clicked
        eventBus.on('vdp.mediaGallery.close', closeMediaGalleryModal);
        return () => {
            eventBus.off('vdp.mediaGallery.close', closeMediaGalleryModal);
        };
    }, [closeMediaGalleryModal]);

    const isMobileLandscape = device?.type !== 'desktop' && device?.lessThan?.md && device?.orientation === 'landscape';
    const seeDetailsLabel = `See ${leasePayment ? 'Lease' : 'Finance'} Details`;
    const headerCtaContent = !isMobileLandscape ? (
        <MediaGalleryCTAs
            emailCTAProps={emailCTAProps}
            handleNDSCTAClick={handleNDSCTAClick}
            phoneClickHandler={phoneClickHandler}
            relocateCTAs={relocateCTAs}
            seeDetailsLabel={seeDetailsLabel}
        />
    ) : null;
    const { thumbnails, viewAllMedia, overlay: overlayMediaSet, fullscreen: fullScreenMediaSet } = getShowcaseOverlayData({
        images: images?.sources || [],
        videos,
        hasFyusion,
        showSpin: fyusion ? hasFyusion : hasId,
        mediaType,
        listingTitle,
        showOverlay,
        isMobileLandscape,
        isXs,
        onShowcaseImgClick: async (e, title, index) => {
            await interactionResponse();
            if (pauseMediaGalleryAudioEye) {
                pauseAudioEye({ length: pauseMediaGalleryAudioEyeLength });
            }

            openOverlay({ tab: title });
            await interactionResponse();
            sendShowcaseItemClick(e, index, title);
        },
        onOverlayClick: async (event, title, index) => {
            await interactionResponse();
            openFullScreen({ index, triggerSpin: true });
            sendAnalyticsEvent('click', 'inventoryMediaViewerClick', { event, index, title, isPsxVdp: isPrivateSeller, par: 'overlay' });
            event.stopPropagation();
        },
        onVideoPlay: () => sendAnalyticsEvent('click', 'inventoryVideoPlay'),
        onVideoCompleted: () => sendAnalyticsEvent('impressions', 'inventoryVideoCompleted'),
        onVideoMilestones: (videoData, milestone) => sendAnalyticsEvent('impressions', 'inventoryVideoMilestones', { milestone }),
        paymentSnapshot: isNDSCalcEnabled && (leasePayment || financePayment) && (
            <div className="margin-vertical-4 text-center padding-bottom-6">
                <div className="margin-bottom-3">
                    <Text
                        size={500}
                        weight="bold"
                        componentClass="div"
                    >
                        Estimated
                        {' '}
                        {leasePayment ? 'Lease' : 'Finance'}
                        {' '}
                        Payment
                    </Text>
                    <Payment
                        uiContext="lender"
                        payment={leasePayment || financePayment}
                    />
                </div>
                {isNDSCalcEnabled && (
                    <CalculatorJumpLink
                        label={seeDetailsLabel}
                        bsStyle="primary"
                        bsSize="small"
                        onClick={(event) => handleNDSCTAClick(event, 'See Details', 'bottom')}
                    />
                )}
            </div>
        ),
    });

    // Wrap the primary image with Save & Share container to show the Save/Share interactive overlay icons
    if (thumbnails.length > 0) {
        const originalElement = thumbnails[0].element;

        thumbnails[0] = {
            ...thumbnails[0],
            element: (
                <SaveAndShareContainer>
                    {originalElement}
                </SaveAndShareContainer>
            ),
        };
    }

    return (
        <div
            suppressHydrationWarning
        >
            {showGhostImage ? (
                <SaveAndShareContainer>
                    <InventoryImagePlaceholder
                        imgClassNames="img-vertically-aligned img-responsive-scale padding-5"
                        height={250}
                    />
                </SaveAndShareContainer>
            ) : (
                <>
                    {(isMediaSliderEnabled && (isSm || isXs))
                        ? (
                            <MediaSlider
                                images={images?.sources}
                                isSm={isSm}
                                key={'slider-' + inventoryId}
                                listingTitle={listingTitle}
                                onImageClick={handleSliderImageClick}
                                onNextButtonClick={() => {
                                    setSliderIndex((currentIndex) => (currentIndex < photoCount ? currentIndex + 1 : photoCount));
                                }}
                                onPrevButtonClick={() => {
                                    setSliderIndex((currentIndex) => (currentIndex > 1 ? currentIndex - 1 : 1));
                                }}
                            />
                        ) : (
                            <MediaShowcase
                                data-cmp="mediaViewer"
                                mediaDataSets={thumbnails}
                                isMobile={isXs}
                                isDesktop={isLg}
                                mediaType={mediaType}
                                onAllMediaClick={async (event) => {
                                    const { value } = event.target;
                                    await interactionResponse();
                                    openOverlay();
                                    await interactionResponse();
                                    event.target.value = value;
                                    sendShowcaseItemClick(event, -1);
                                }}
                                showAllMediaButton
                                showAllMediaImage={viewAllMedia?.element}
                            />
                        )}

                    {isMediaSliderEnabled && (
                        <MediaNavigator
                            isXs={isXs}
                            isSm={isSm}
                            onSelectTab={(tab) => openOverlay({ tab })}
                            hasSpin={hasId || hasFyusion}
                            photoCount={photoCount}
                            videoCount={videoCount}
                            sliderIndex={sliderIndex}
                        />
                    )}

                    {showOverlay && (
                        <MediaGalleryModal
                            defaultActiveKey={selectedTab.current === 'Photos' ? 0 : 1}
                            headerCtaContent={headerCtaContent}
                            hideMediaGaleryModal={closeOverlay}
                            modalTitle={listingTitle}
                            loadSpin={loadSpin}
                            overlayMediaSet={overlayMediaSet}
                            pricingDetail={pricingDetail}
                            relocateCTAs={relocateCTAs}
                            show={showOverlay}
                            showFullScreen={showFullScreen}
                            isMobile={isXs}
                            onTabClick={(title = '') => {
                                sendAnalyticsEvent('click', 'mediaTabClick', { title });
                            }}
                        />
                    )}
                    {showFullScreen && (
                        <FullscreenViewer
                            isMobile={isXs}
                            headerCtaContent={headerCtaContent}
                            show={showFullScreen}
                            closeFullScreen={closeFullScreen}
                            fullscreenDataSet={fullScreenMediaSet}
                            defaultIndex={fullScreenIndex?.current}
                            onStateUpdateCallback={(currentSlide) => {
                                if (currentSlide !== fullScreenIndex?.current) {
                                    sendAnalyticsEvent('click', 'mediaViewerPagination', {
                                        currentSlide,
                                        fullScreenIndex: fullScreenIndex?.current || 0,
                                    });
                                    updateFullScreenIndex(currentSlide);
                                }
                            }}
                            isPsxVdp={isPrivateSeller}
                            relocateCTAs={relocateCTAs}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default memo(MediaGallery, _isEqual);
